import { useCallback, useRef } from "react"

const DEFAULT_MAX_GAP_MS = 450
const DEFAULT_SEQUENCE_RESET_MS = 2500

export interface RapidTapUnlockOptions {
  requiredTaps: number
  /** Max ms between consecutive taps; a slower tap resets the counter. */
  maxGapMs?: number
  /** Max ms since the first tap in a burst; exceeding resets the counter. */
  sequenceResetMs?: number
  onUnlock: () => void
}

/**
 * Counts rapid taps (e.g. avatar easter egg). Returns a handler to attach to click targets.
 */
export function useRapidTapUnlock({
  requiredTaps,
  maxGapMs = DEFAULT_MAX_GAP_MS,
  sequenceResetMs = DEFAULT_SEQUENCE_RESET_MS,
  onUnlock,
}: RapidTapUnlockOptions) {
  const tapCountRef = useRef(0)
  const lastTapAtRef = useRef(0)
  const sequenceStartRef = useRef(0)

  return useCallback(() => {
    const now = Date.now()
    const lastTap = lastTapAtRef.current
    const sequenceStart = sequenceStartRef.current

    if (
      tapCountRef.current > 0 &&
      ((lastTap > 0 && now - lastTap > maxGapMs) ||
        (sequenceStart > 0 && now - sequenceStart > sequenceResetMs))
    ) {
      tapCountRef.current = 0
      sequenceStartRef.current = 0
    }

    if (tapCountRef.current === 0) {
      sequenceStartRef.current = now
    }

    tapCountRef.current += 1
    lastTapAtRef.current = now

    if (tapCountRef.current >= requiredTaps) {
      tapCountRef.current = 0
      lastTapAtRef.current = 0
      sequenceStartRef.current = 0
      onUnlock()
    }
  }, [maxGapMs, onUnlock, requiredTaps, sequenceResetMs])
}
